import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Lock, User, Globe, AlertCircle, Loader2, ChevronDown, Smartphone, Mail, ArrowLeft, ShieldCheck } from 'lucide-react';
import { useAuth } from './AuthContext';

const Login = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { login } = useAuth();
  
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const langMenuRef = useRef(null);

  // 🎯 حالات الـ 2FA
  const [requires2FA, setRequires2FA] = useState(false);
  const [twoFactorMethod, setTwoFactorMethod] = useState('none');
  const [verificationCode, setVerificationCode] = useState('');

  // لغات السيستم الـ 11
  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸', dir: 'ltr' },
    { code: 'ar', name: 'العربية', flag: '🇪🇬', dir: 'rtl' },
    { code: 'fr', name: 'Français', flag: '🇫🇷', dir: 'ltr' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪', dir: 'ltr' }, 
    { code: 'it', name: 'Italiano', flag: '🇮🇹', dir: 'ltr' }, 
    { code: 'es', name: 'Español', flag: '🇪🇸', dir: 'ltr' },  
    { code: 'zh', name: '中文', flag: '🇨🇳', dir: 'ltr' },   
    { code: 'ja', name: '日本語', flag: '🇯🇵', dir: 'ltr' },   
    { code: 'ko', name: '한국어', flag: '🇰🇷', dir: 'ltr' },   
    { code: 'ru', name: 'Русский', flag: '🇷🇺', dir: 'ltr' }, 
    { code: 'ur', name: 'اُردُو', flag: '🇵🇰', dir: 'rtl' }   
  ];

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang.code);
    document.documentElement.dir = lang.dir;
    localStorage.setItem('i18nextLng', lang.code);
    localStorage.setItem('layoutDir', lang.dir);
    setIsLangMenuOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (langMenuRef.current && !langMenuRef.current.contains(e.target)) setIsLangMenuOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleAuthSuccess = (userData, token) => {
    const userObj = {
      username: username,
      display_name: userData?.display_name || "Administrator",
      role: userData?.role,
      org_id: userData?.org_id || '',
      site_id: userData?.site_id,
      email: userData?.email || '',
      is_2fa_enabled: userData?.is_2fa_enabled || false,
      two_factor_method: userData?.two_factor_method || 'none',
      currency: userData?.currency || 'EGP',
      initial: (userData?.username || "A").charAt(0).toUpperCase()
    };

    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userObj));
    login(userObj);
    // 🎯 التوجيه المخصص: لو اليوزر sysadmin يروح لـ STM، غير كده يروح للداشبورد العادية
    if (username.toLowerCase() === 'sysadmin') {
      navigate('/organizations/STM');
    } else {
      navigate('/dashboard');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const params = new URLSearchParams();
    params.append('username', username);
    params.append('password', password);

    try {
      const response = await axios.post('/api/token', params, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      });
      
      // 🎯 فحص إذا كان المستخدم مفعل الـ 2FA
      if (response.data?.status === 'requires_2fa') {
        setTwoFactorMethod(response.data.method);
        setRequires2FA(true);
        setLoading(false);
        return;
      }

      // الدخول العادي لو مفيش 2FA
      handleAuthSuccess(response.data?.user, response.data?.access_token);
      
    } catch (err) {
      setError(t('login.error_msg') || 'Invalid credentials'); 
      setLoading(false);
    }
  };

  // 🎯 دالة إرسال كود الـ 2FA
  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post('/api/login/verify-2fa', {
        username: username,
        password: password,
        code: verificationCode
      });

      handleAuthSuccess(response.data?.user, response.data?.access_token);
    } catch (err) {
      setError(t('login.invalid_2fa_code') || "Invalid Verification Code");
      setVerificationCode('');
    } finally {
      setLoading(false);
    }
  };

  // العودة لشاشة اللوجين الرئيسية
  const handleBackToLogin = () => {
    setRequires2FA(false);
    setVerificationCode('');
    setPassword('');
    setError('');
  };

  return (
    // 🎯 هنا الإبداع: الخلفية والأنيميشن البطيء (Ken Burns effect)
    <div className={`min-h-screen flex items-center justify-center relative overflow-hidden ${i18n?.language === 'ar' || i18n?.language === 'ur' ? 'font-cairo' : 'font-sans'}`}>
      
      {/* 🖼️ الخلفية الثابتة مع حركة بطيئة */}
      <div 
        className="absolute inset-0 z-0 bg-cover bg-center animate-[kenburns_20s_ease-in-out_infinite_alternate]"
        style={{
          // استخدمت الصورة التانية للمدينة الذكية لأنها الأفضل في التباين
          backgroundImage: "url('/assets/bg-login.png')", // 👈 حط مسار الصورة هنا (مثلاً الصورة التانية اللي بعتها)
        }}
      ></div>

      {/* ⬛ طبقة شبه شفافة لتقليل حدة الصورة وإبراز المربع */}
      <div className="absolute inset-0 z-0 bg-[#001c38]/60 backdrop-blur-[2px]"></div>
      
      {/* الخط العلوي الرفيع */}
      <div className="absolute top-0 left-0 w-full h-1.5 bg-[#005495] z-10 shadow-lg"></div>

      {/* 🌐 زر اختيار اللغة */}
      <div className="absolute top-6 right-6 z-20" ref={langMenuRef}>
        <button 
          onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
          className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md border border-white/20 rounded shadow-lg text-white hover:bg-white/20 transition-all font-bold text-sm"
        >
          <Globe size={16} />
          <span className="uppercase tracking-widest">{i18n?.language || 'EN'}</span>
          <ChevronDown size={14} className={`transition-transform ${isLangMenuOpen ? 'rotate-180' : ''}`} />
        </button>

        {isLangMenuOpen && (
          <div className={`absolute top-12 ${i18n?.language === 'ar' || i18n?.language === 'ur' ? 'left-0' : 'right-0'} w-40 bg-white/95 backdrop-blur-xl border border-white/20 shadow-2xl rounded overflow-hidden z-[100] max-h-64 overflow-y-auto`}>
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => changeLanguage(lang)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-xs font-bold hover:bg-[#005495] hover:text-white transition-colors ${i18n?.language === lang.code ? 'bg-blue-50 text-[#005495]' : 'text-gray-700'}`}
              >
                <span className="text-base">{lang.flag}</span>
                <span className="uppercase tracking-wider">{lang.name}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* 📦 مربع تسجيل الدخول المطور */}
      <div className="w-full max-w-[420px] z-10 p-4">
        <div className="bg-white/95 backdrop-blur-xl border border-white/40 shadow-[0_20px_50px_rgba(0,0,0,0.5)] rounded-lg overflow-hidden relative">
          
          <div className="bg-gradient-to-b from-[#f5ffff] to-white p-8 text-center border-b border-gray-200/50">
            {/* اللوجو */}
            <img src="/assets/teknova.png" alt="Logo" className="inline-block hover:scale-105 transition-transform duration-500" width="180px"/>
            <p className="text-[#005495] text-[10px] mt-2 font-black uppercase tracking-[0.4em] opacity-80">Just Smart</p>
          </div>

          <div className="p-8">
            {error && (
              <div className="bg-red-50/90 backdrop-blur border-l-4 border-red-500 p-3 mb-6 flex items-center gap-3 animate-in slide-in-from-top-2">
                <AlertCircle className="text-red-500 min-w-[18px]" size={18} />
                <span className="text-red-800 text-[11px] font-bold">{error}</span>
              </div>
            )}

            {/* 🎯 الشاشة العادية */}
            {!requires2FA ? (
              <form onSubmit={handleLogin} className="space-y-6 animate-in slide-in-from-left-4 fade-in duration-300">
                <div>
                  <label className={`text-[10px] font-black text-gray-500 uppercase mb-2 block tracking-widest ${i18n?.language === 'ar' || i18n?.language === 'ur' ? 'text-right' : 'text-left'}`}>
                    {t('username', 'Username')}
                  </label>
                  <div className="relative group">
                    <div className={`absolute inset-y-0 ${i18n?.language === 'ar' || i18n?.language === 'ur' ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none text-gray-400 group-focus-within:text-[#005495] transition-colors`}>
                      <User size={18} />
                    </div>
                    <input 
                      type="text" required
                      className={`w-full bg-gray-50/50 border-b-2 border-gray-200 focus:border-[#005495] focus:bg-white py-3 outline-none transition-all text-sm font-bold ${i18n?.language === 'ar' || i18n?.language === 'ur' ? 'pr-10 text-right' : 'pl-10 text-left'}`}
                      placeholder={t('login.id_placeholder', 'Enter your ID')}
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                    />
                  </div>
                </div>

                <div>
                  <label className={`text-[10px] font-black text-gray-500 uppercase mb-2 block tracking-widest ${i18n?.language === 'ar' || i18n?.language === 'ur' ? 'text-right' : 'text-left'}`}>
                    {t('password', 'Password')}
                  </label>
                  <div className="relative group">
                    <div className={`absolute inset-y-0 ${i18n?.language === 'ar' || i18n?.language === 'ur' ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none text-gray-400 group-focus-within:text-[#005495] transition-colors`}>
                      <Lock size={18} />
                    </div>
                    <input 
                      type="password" required
                      className={`w-full bg-gray-50/50 border-b-2 border-gray-200 focus:border-[#005495] focus:bg-white py-3 outline-none transition-all text-sm font-bold tracking-widest ${i18n?.language === 'ar' || i18n?.language === 'ur' ? 'pr-10 text-right' : 'pl-10 text-left'}`}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                  </div>
                </div>

                <button 
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-gradient-to-r from-[#005495] to-[#003d6d] hover:from-[#003d6d] hover:to-[#002a4a] text-white font-black rounded shadow-[0_4px_15px_rgba(0,84,149,0.3)] transition-all active:scale-[0.98] flex items-center justify-center gap-2 uppercase tracking-widest text-[11px]"
                >
                  {loading ? <Loader2 className="animate-spin" size={20} /> : t('login_btn', 'SECURE LOGIN')}
                </button>
              </form>
            ) : (
              /* 🎯 شاشة إدخال كود المصادقة 2FA */
              <form onSubmit={handleVerify2FA} className="space-y-6 animate-in slide-in-from-right-4 fade-in duration-300">
                <div className="flex flex-col items-center justify-center text-center space-y-3 mb-6">
                  <div className="w-16 h-16 bg-blue-50 border-2 border-blue-100 rounded-full flex items-center justify-center text-[#005495] shadow-inner">
                    {twoFactorMethod === 'authenticator' ? <Smartphone size={32} /> : <Mail size={32} />}
                  </div>
                  <h3 className="font-black text-[#1a3353] text-lg uppercase tracking-widest">Verification</h3>
                  <p className="text-xs text-gray-500 font-bold px-4 leading-relaxed">
                    {twoFactorMethod === 'authenticator' 
                      ? "Enter the 6-digit code from your Authenticator app."
                      : "We've sent a 6-digit code to your registered email."}
                  </p>
                </div>

                <div>
                  <input 
                    type="text" required
                    maxLength={6}
                    autoFocus
                    className="w-full bg-gray-50 border-2 border-gray-200 focus:border-[#005495] focus:bg-white rounded py-3 text-center outline-none transition-colors text-2xl font-mono tracking-[0.5em] text-[#005495] font-bold shadow-inner"
                    placeholder="------"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                  />
                </div>

                <div className="flex flex-col gap-3">
                  <button 
                    type="submit"
                    disabled={loading || verificationCode.length !== 6}
                    className="w-full h-12 bg-gradient-to-r from-[#005495] to-[#003d6d] disabled:from-gray-400 disabled:to-gray-500 text-white font-black rounded shadow-md transition-all active:scale-[0.98] flex items-center justify-center gap-2 uppercase tracking-widest text-[11px]"
                  >
                    {loading ? <Loader2 className="animate-spin" size={20} /> : <><ShieldCheck size={16}/> VERIFY</>}
                  </button>

                  <button 
                    type="button"
                    onClick={handleBackToLogin}
                    disabled={loading}
                    className="w-full h-10 bg-white border border-gray-300 text-gray-500 hover:bg-gray-50 hover:text-gray-800 font-bold rounded transition-all flex items-center justify-center gap-2 uppercase tracking-widest text-[10px]"
                  >
                    <ArrowLeft size={14} /> Back
                  </button>
                </div>
              </form>
            )}

            <div className="mt-8 flex justify-between items-center text-[10px] text-gray-400 border-t border-gray-100 pt-6">
              <button type="button" className="hover:text-[#005495] transition-colors uppercase font-black tracking-widest">
                {t('forgot_pwd', 'Forgot Password?')}
              </button>
              <span className="font-mono font-bold bg-gray-100 px-2 py-0.5 rounded">v4.2.0</span>
            </div>
          </div>
        </div>
        
        {/* Footer Text */}
        <p className="text-center text-[10px] text-white/50 mt-6 uppercase tracking-[0.3em] font-black drop-shadow-md">
          Powered by TEKNOVA © {new Date().getFullYear()}
        </p>
      </div>

      {/* 🎯 ستايل الـ Animation في نفس الملف للسهولة */}
      <style dangerouslySetInnerHTML={{__html: `
        @keyframes kenburns {
          0% { transform: scale(1) translate(0, 0); }
          100% { transform: scale(1.1) translate(-1%, -1%); }
        }
      `}} />
    </div>
  );
};

export default Login;